import { ErrorHandler, Injectable } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable ,  of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AviationEvent } from '../models/aviation-event.model';
import { LatLon } from '../../util/geodesy/latlon';
import { BoundingBox } from '../../util/geodesy/bounding-box';
import { IEntity } from '../models/entity.model';
import { Photo } from '../models/photo.model';
import { Country } from '../models/country.model';
import { Timezone } from '../models/timezone.model';

/**
 * Minimum distance when performing a radius search (nautical miles)
 */
export const MIN_RADIUS = 25;


/**
 * Maximum distance when performing a radius search (nautical miles)
 */
export const MAX_RADIUS = 500;


@Injectable({
  providedIn: 'root'
})
export class AviationEventsService {

  constructor(private _http: HttpClient,
              private _decimalPipe: DecimalPipe,
              private _errorHandler: ErrorHandler) {
  }


  /**
   * Returns a sanitized search term for airport search, with all invalid characters removed.
   * @param {string} term Original input term
   * @returns {string} Sanitized term
   */
  static sanitizeSearchTerm(term: string): string {
    return (term || '').toString().replace(/[^a-z0-9 ]+/gi, '').trim();
  }

  /**
   * Returns all aviation events
   * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
   * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
   * @returns {Promise<AviationEvent[]>} List of aviation events
   */
  async getAviationEvents(startDateUTC: string, endDateUTC: string): Promise<AviationEvent[]> {
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, {
        startDate: startDateUTC,
        endDate: endDateUTC
      }).toPromise();
      return response.map(AviationEvent.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns details of a specific aviation event.
   * @param aviationEventId {number} Event id to return details for
   * @returns {Promise<AviationEvent>} aviation event returned
   */
  async getAviationEventById(aviationEventId: number): Promise<AviationEvent> {
    try {
      const response = await this._http.get(`${environment.airportsApiUrl}/events/${aviationEventId}`).toPromise();
      return AviationEvent.parse(response);
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns aviation events for a given search term.
   * @param term {string} Search term
   * @returns {Promise<AviationEvent[]>} List of matching aviation events
   */
  async getAviationEventsBySearchTerm(term: string): Promise<AviationEvent[]> {
    term = AviationEventsService.sanitizeSearchTerm(term);
    if (term.length < 3) {
      throw new Error('At least three alphanumeric characters are necessary to perform a search.');
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, {
        searchType: 'Basic',
        searchTerm: `%${term}%`,
      }).toPromise();
      return response.map(AviationEvent.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns aviation events for a comma separated list of airport IDs.
   * @param airportList {string} Search term
   * @returns {Promise<AviationEvent[]>} List of matching aviation events
   */
  async getFavoriteAviationEvents(airportList: string): Promise<AviationEvent[]> {
    if (airportList.length < 3) {
      throw new Error('At least three alphanumeric characters are necessary to perform a search.');
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, {
        searchType: 'Favorites',
        searchTerm: airportList
      }).toPromise();
      return response.map(AviationEvent.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Performs a radial search for aviation events within a given distance of a center point.
   * @param latlon {LatLon} Center point of search
   * @param radius {number} Radius in nautical miles
   * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
   * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
   * @returns {Promise<AviationEvent[]>} List of matching aviation events
   */
  async getAviationEventsByLatLon(latlon: LatLon, radius: number, startDateUTC: string = null, endDateUTC: string = null):
    Promise<AviationEvent[]> {
    if (!latlon) {
      throw new Error('latlon is required');
    }
    if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
      throw new Error(`radius is required and must be between ${MIN_RADIUS} and ${MAX_RADIUS} nautical miles.`);
    }
    try {
      let body;
      if (startDateUTC && endDateUTC) {
        body = {
          point: {
            latitude: latlon.lat,
            longitude: latlon.lon
          },
          radius: radius,
          startDate: startDateUTC,
          endDate: endDateUTC
        };
      } else {
        body = {
          point: {
            latitude: latlon.lat,
            longitude: latlon.lon
          },
          radius: radius
        };
      }
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, body).toPromise();
      return response.map(AviationEvent.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Performs a bounding box search for aviation events
   * @param bounds {BoundingBox} Bounding box
   * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
   * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
   * @returns {Promise<AviationEvent[]>} List of matching aviation events
   */
  async getAviationEventsByBoundingBox(bounds: BoundingBox, startDateUTC: string, endDateUTC: string): Promise<AviationEvent[]> {
    const area = bounds.getAreaDegrees();
    try {
      const neLat = bounds.ne.lat.toFixed(7);
      const neLon = bounds.ne.lon.toFixed(7);
      const swLat = bounds.sw.lat.toFixed(7);
      const swLon = bounds.sw.lon.toFixed(7);
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, {
        rectangleLatLong: `${neLat},${neLon},${swLat},${swLon}`,
        startDate: startDateUTC,
        endDate: endDateUTC
      }).toPromise();
      return response.map(AviationEvent.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns all aviation events within a given state
   * @param state {string} State code
   * @param startDateUTC {string} the start date of the event in UTC in ISO8601 string format
   * @param endDateUTC {string} the end date of the event in UTC in ISO8601 string format
   * @returns {Promise<AviationEvent[]>} List of matching aviation events
   */
  async getAviationEventsByState(state: string, startDateUTC: string, endDateUTC: string): Promise<AviationEvent[]> {
    if (!state) {
      throw new Error('"state" parameter is required when searching aviation events by state');
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, {
        stateProvince: state,
        startDate: startDateUTC,
        endDate: endDateUTC
      }).toPromise();
      return response.map(AviationEvent.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * adds event
   * @returns {Promise<AviationEvent>} returns event after adding it
   * @param event
   */
  async addAviationEvent(event: AviationEvent): Promise<AviationEvent> {
    if (!event) {
      throw new Error('"event" parameter is required');
    }
    const response = await this._http.post<AviationEvent>(`${environment.airportsApiUrl}/event`, event).toPromise();
    return AviationEvent.parse(response);
  }

  async uploadEventCoverImg(image: File, eventId: number): Promise<AviationEvent> {
    if (!image) {
      throw new Error('"event" parameter is required');
    }
    try {
      const contentDisposition = 'form-data; name="' + image.name + '"; filename="' + image.name + '"';
      const uploadData = new FormData();
      // uploadData.set('picture', image);
      uploadData.set('category', 'event');
      uploadData.set('eventId', eventId.toString());
      // uploadData.set(this.selectedFile.name, this.selectedFile.name);
      uploadData.append('File', image, image.name);
      uploadData.set(image.name, image.name);
      return await this._http.post<AviationEvent>(`${environment.airportsApiUrl}/photos/event/upload`,
        uploadData,
        {
          headers: {
            'Content-Disposition': contentDisposition
          }
        }).toPromise();
    } catch (e) {
      console.error(e);
    }
  }

  async putEventPhoto(eventId: number, photoId: number): Promise<Photo> {
    try {
      let response: any;
      const BODY = this._getEventPhotoJSON(photoId, eventId);
      response = await this._http.put(`${environment.airportsApiUrl}/photos/event/update/${photoId}`, BODY).toPromise();
      return Photo.parse(response);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  _getEventPhotoJSON (photoId: number, eventId: number): any {
    return {
      photoId: photoId,
      entityId: eventId
    };
  }

  deleteEventCoverImg(eventId: number, photoId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/photos/event/delete/${photoId}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
    * Returns aviation events that match a list of event ids
    * @returns {Promise<AviationEvent[]>} List of aviation events
    */
  async getFavoriteEvents(eventIds: string): Promise<AviationEvent[]> {
    try {
      if (eventIds.length > 0) {
        const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/events`, {
        }).toPromise();
        const aviationEvents = response.map(AviationEvent.parse);
        const eventList = eventIds.split(',');
        const favoriteEvents = [];
        aviationEvents.forEach((ae) => {
          if (eventList.includes(ae.eventId.toString())) {
            favoriteEvents.push(ae);
          }
        });
        return favoriteEvents;
      } else {
        return null;
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * updates event
   * @returns {Promise<AviationEvent>} returns event after updating it
   * @param event
   */
  async updateAviationEvent(event: AviationEvent): Promise<AviationEvent> {
    if (!event) {
      throw new Error('"event" parameter is required');
    }
    event.interactions = null;
    const response = await this._http.put<AviationEvent>(`${environment.airportsApiUrl}/events/${event.eventId}`,
      event).toPromise();
    return AviationEvent.parse(response);
  }

  deleteAviationEvent(eventId: number): Promise<any> {
    const URL = `${environment.airportsApiUrl}/events/${eventId}`;
    try {
      return this._http.delete(URL).toPromise();
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * clones an aviation event in the database using the new dates passed in
   * @returns {Promise<AviationEvent>} returns new cloned event information
   * @param eventId {number} the eventId to create a clone of
   * @param startDateTimeUTC {string} the start date of the event in UTC in ISO8601 string format
   * @param endDateTimeUTC {string} the end date of the event in UTC in ISO8601 string format
   * @param recId {number} the recId of the user attempting to clone the event
   */
  async cloneAviationEvent(eventId: number, startDateTimeUTC: string, endDateTimeUTC: string, recId: number): Promise<AviationEvent> {
    const body = {
      'startDateTimeUtc': startDateTimeUTC,
      'endDateTimeUtc': endDateTimeUTC,
      'recId': recId
    };
    const response = await this._http.post<AviationEvent>(`${environment.airportsApiUrl}/events/${eventId}/clone`,
      body).toPromise();
    return AviationEvent.parse(response);
  }

  /**
   * Returns list of countries
   * @returns {Promise<Country[]>} List of countries
   */
  async getCountries(): Promise<Country[]> {
    try {
        const response = await this._http.get<any[]>(`${environment.airportsApiUrl}/events/countries`, {
        }).toPromise();
        const countries = response.map(Country.parse);
        if (countries && countries.length > 0) {
          return countries;
        } else {
          return [];
        }
      } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns list of timezones for a country
   * @returns {Promise<Timezone[]>} List of timezones
   * @param countryCode {string} the country to get timezones for
   */
  async getTimezones(countryCode: string): Promise<Timezone[]> {
    try {
      const response = await this._http.get<any[]>(`${environment.airportsApiUrl}/events/timezones/${countryCode}`, {
      }).toPromise();
      const timezones = response.map(Timezone.parse);
      if (timezones && timezones.length > 0) {
        return timezones;
      } else {
        return [];
      }
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

}

